import { Link } from "react-router-dom";
import { InfinityLogo } from "@/components/InfinityLogo";

/** One botanical leaf: pointed tip, petiole, midrib, side veins. Local box 24×32. */
function LeafBlade({ opacity = 1 }) {
  return (
    <g opacity={opacity}>
      <path
        d="M12 31C12 31 11.6 28.2 12 26.2C6.2 25.2 2.2 20.2 2.8 13.2C3.4 6.4 8.2 1.8 12 1.2C15.8 1.8 20.6 6.4 21.2 13.2C21.8 20.2 17.8 25.2 12 26.2C12.4 28.2 12 31 12 31Z"
        fill="currentColor"
      />
      <path d="M12 26.2V5.4" stroke="#06110A" strokeOpacity="0.38" strokeWidth="1.15" strokeLinecap="round" />
      <path d="M12 9.2C9 10.4 6.6 12.6 5.4 15" stroke="#06110A" strokeOpacity="0.28" strokeWidth="0.9" strokeLinecap="round" fill="none" />
      <path d="M12 14C9.2 15.2 7.2 17.4 6.4 20" stroke="#06110A" strokeOpacity="0.26" strokeWidth="0.9" strokeLinecap="round" fill="none" />
      <path d="M12 18.8C9.6 19.8 8.2 21.4 7.4 23.2" stroke="#06110A" strokeOpacity="0.22" strokeWidth="0.85" strokeLinecap="round" fill="none" />
      <path d="M12 9.2C15 10.4 17.4 12.6 18.6 15" stroke="#06110A" strokeOpacity="0.22" strokeWidth="0.9" strokeLinecap="round" fill="none" />
      <path d="M12 14C14.8 15.2 16.8 17.4 17.6 20" stroke="#06110A" strokeOpacity="0.2" strokeWidth="0.9" strokeLinecap="round" fill="none" />
      <path d="M12 18.8C14.4 19.8 15.8 21.4 16.6 23.2" stroke="#06110A" strokeOpacity="0.18" strokeWidth="0.85" strokeLinecap="round" fill="none" />
    </g>
  );
}

export function LeafShape({ className = "w-6 h-6" }) {
  return (
    <svg viewBox="0 0 24 32" className={className} fill="none" aria-hidden>
      <LeafBlade />
    </svg>
  );
}

/** Two real leaves sharing a stem — reads as infinity and still looks like foliage. */
export function LeafInfinity({ className = "w-5 h-5" }) {
  return (
    <svg viewBox="0 0 56 36" className={className} fill="none" aria-hidden>
      <g transform="translate(26 20) rotate(-52)">
        <g transform="translate(-12 -29)">
          <LeafBlade opacity={1} />
        </g>
      </g>
      <g transform="translate(30 20) rotate(52)">
        <g transform="translate(-12 -29)">
          <LeafBlade opacity={0.78} />
        </g>
      </g>
    </svg>
  );
}

export function BrandMark({ size = "md" }) {
  const dims = size === "lg" ? { width: 51, height: 30 } : size === "sm" ? { width: 34, height: 20 } : { width: 42, height: 25 };
  return <InfinityLogo {...dims} />;
}

export function Brand({ size = "md", to }) {
  const inner = (
    <div className="flex items-center gap-2.5 min-w-0" data-testid="landing-logo">
      <BrandMark size={size} />
      <BrandLockup size={size} />
    </div>
  );
  return to ? <Link to={to} className="min-w-0">{inner}</Link> : inner;
}

export function EarthGlobe({ size = 168, caption, compact = false }) {
  const dim = compact ? 88 : size;
  return (
    <div className="flex flex-col items-center gap-3" data-testid="earth-globe">
      <div className="earth-stage" style={{ width: dim, height: dim }}>
        <div className="earth-orbit" aria-hidden>
          <span className="earth-sat" />
        </div>
        <div className="earth-sphere">
          <div className="earth-land" />
          <div className="earth-grid" />
          <div className="earth-shine" />
        </div>
      </div>
      {caption && <p className="text-xs text-slate-400 text-center max-w-[220px]">{caption}</p>}
    </div>
  );
}

export function LeafLoader({ label = "Loading…", compact = false }) {
  return (
    <div className={`flex flex-col items-center justify-center ${compact ? "gap-2" : "gap-3 py-10"}`} data-testid="leaf-loader">
      <div className={`leaf-breathe ${compact ? "w-10 h-10" : "w-14 h-14"} flex items-center justify-center`}>
        <InfinityLogo width={compact ? 34 : 48} height={compact ? 20 : 28} />
      </div>
      {label && <p className="text-xs text-slate-500 font-mono uppercase tracking-wider">{label}</p>}
    </div>
  );
}

export function PageLoader({ label = "Loading…" }) {
  return (
    <div className="min-h-screen bg-[var(--page)] flex items-center justify-center">
      <LeafLoader label={label} />
    </div>
  );
}

export const Waveform = ({ bars = 9, className = "", color = "bg-emerald-400" }) => (
  <div className={`flex items-center gap-[3px] h-6 ${className}`} aria-hidden>
    {Array.from({ length: bars }).map((_, i) => (
      <span key={i} className={`wave-bar w-[3px] h-full rounded-full ${color}`} style={{ animationDelay: `${(i % 5) * 0.12}s`, opacity: 0.6 + (i % 3) * 0.15 }} />
    ))}
  </div>
);

export const PageHeader = ({ overline, title, desc, action, testId }) => (
  <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8 rise" data-testid={testId}>
    <div>
      {overline && <p className="overline-label mb-2">{overline}</p>}
      <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-slate-50">{title}</h1>
      {desc && <p className="text-sm text-slate-400 mt-1.5 max-w-2xl">{desc}</p>}
    </div>
    {action}
  </div>
);

export const Stat = ({ label, value, sub, accent = "emerald", testId, icon: Icon }) => {
  const colors = { emerald: "text-emerald-300", gold: "text-amber-300", cyan: "text-cyan-300", slate: "text-slate-100", red: "text-rose-300" };
  return (
    <div className="glass p-5 rise" data-testid={testId}>
      <div className="flex items-center justify-between mb-3">
        <p className="text-[11px] font-mono uppercase tracking-wider text-slate-500">{label}</p>
        {Icon && <Icon className="w-4 h-4 text-slate-500" />}
      </div>
      <p className={`font-display text-2xl sm:text-3xl font-bold ${colors[accent]}`}>{value}</p>
      {sub && <p className="text-xs text-slate-500 mt-1">{sub}</p>}
    </div>
  );
};

export const Empty = ({ title, desc, cta, to, onClick, testId }) => (
  <div className="glass p-10 text-center rise" data-testid={testId}>
    <div className="w-12 h-12 rounded-2xl bg-emerald-500/10 border border-emerald-500/20 mx-auto mb-4 flex items-center justify-center"><span className="w-2.5 h-2.5 rounded-full bg-emerald-400" /></div>
    <h3 className="font-display text-lg font-semibold text-slate-100">{title}</h3>
    <p className="text-sm text-slate-400 mt-1.5 max-w-md mx-auto">{desc}</p>
    {cta && (to ? <Link to={to} className="btn-primary mt-5" data-testid={`${testId}-cta`}>{cta}</Link> : <button onClick={onClick} className="btn-primary mt-5" data-testid={`${testId}-cta`}>{cta}</button>)}
  </div>
);

export function BrandLockup({ size = "md" }) {
  return (
    <div>
      <p className={`font-display font-bold text-ink leading-none ${size === "lg" ? "text-lg" : "text-[15px]"}`}>AgriVoice</p>
    </div>
  );
}

export const StatusPill = ({ status }) => {
  const map = {
    running: "pill-gold", completed: "pill", draft: "pill-muted", scheduled: "pill-cyan", published: "pill", active: "pill",
    "PO raised": "pill-gold", "SO created": "pill", "Handled externally": "pill-muted", order_raised: "pill-gold", confirmed: "pill", no_order: "pill-muted", not_reached: "pill-muted", callback: "pill-cyan", success: "pill", mock_sent: "pill-cyan", failed: "pill-muted", skipped: "pill-muted",
  };
  return <span className={map[status] || "pill-muted"} data-testid={`status-${status}`}>{String(status).replace(/_/g, " ")}</span>;
};

export const Modal = ({ open, onClose, title, children, wide, testId }) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-6 bg-[#040705]/85 backdrop-blur-sm" onClick={onClose} data-testid={testId}>
      <div className={`glass w-full ${wide ? "max-w-3xl" : "max-w-lg"} max-h-[92vh] overflow-y-auto p-6 rounded-b-none sm:rounded-2xl rise`} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-5">
          <h3 className="font-display text-lg font-semibold text-slate-50">{title}</h3>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-200 text-xl leading-none" data-testid="modal-close-btn">×</button>
        </div>
        {children}
      </div>
    </div>
  );
};

export const Select = ({ value, onChange, options, testId, placeholder, className = "" }) => (
  <select value={value || ""} onChange={(e) => onChange(e.target.value)} className={`field appearance-none ${className}`} data-testid={testId}>
    {placeholder && <option value="">{placeholder}</option>}
    {options.map((o) => (typeof o === "string" ? <option key={o} value={o}>{o}</option> : <option key={o.value} value={o.value}>{o.label}</option>))}
  </select>
);

export const Toggle = ({ on, onChange, testId, disabled, title }) => (
  <button
    type="button"
    role="switch"
    aria-checked={on}
    disabled={disabled}
    title={title}
    onClick={() => !disabled && onChange(!on)}
    data-testid={testId}
    className={`relative inline-flex w-10 h-6 shrink-0 items-center rounded-full border transition-colors disabled:opacity-50 disabled:pointer-events-none ${on ? "bg-emerald-500 border-emerald-600/50" : "bg-slate-400/50 border-slate-500/40"}`}
  >
    <span className={`absolute top-[3px] left-[3px] w-[18px] h-[18px] rounded-full bg-white shadow-[0_1px_3px_rgba(15,23,42,0.35)] transition-transform ${on ? "translate-x-4" : "translate-x-0"}`} />
  </button>
);
